import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "./RentPage.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const RentPage = ({ filters }) => {
  const [rentPosts, setRentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingBike, setPendingBike] = useState(null);

  // Tracks which bikes have their contact unlocked (boolean per bike)
  const [unlockedContacts, setUnlockedContacts] = useState({});
  // Total contact view credits from backend
  const [contactsUnlockedCount, setContactsUnlockedCount] = useState(0);

  const userInfo = JSON.parse(localStorage.getItem("user"));
  const isLoggedIn = !!userInfo;
  const cardsPerPage = 9;

  // Fetch Rent posts
  useEffect(() => {
    const fetchRentPosts = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/post/rent`);
        const data = await res.json();
        const filteredPosts = data
          .filter((post) => post.postType === "Rent")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setRentPosts(filteredPosts);
      } catch (err) {
        console.error("Error fetching rent posts:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRentPosts();
  }, []);

  // Fetch user's contact unlock credits on mount
  useEffect(() => {
    if (isLoggedIn && userInfo?.token) {
      fetch(`${process.env.REACT_APP_API_URL}/api/payment/contacts`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setContactsUnlockedCount(data.contactsUnlocked || 0);
          }
        })
        .catch((err) => console.error("Failed to fetch contact credits:", err));
    }
  }, [isLoggedIn, userInfo?.token]);

  // Filter posts based on filters
  const filteredRentPosts = rentPosts.filter((post) => {
    const pricePerDay =
      parseInt((post.pricePerDay || "0").toString().replace(/,/g, ""), 10) || 0;
    const pricePerWeek =
      parseInt((post.pricePerWeek || "0").toString().replace(/,/g, ""), 10) || 0;

    return (
      (!filters.location ||
        post.state?.toLowerCase().includes(filters.location.toLowerCase()) ||
        post.area?.toLowerCase().includes(filters.location.toLowerCase()) ||
        post.city?.toLowerCase().includes(filters.location.toLowerCase()) ||
        String(post.pincode || "").includes(filters.location)) &&
      (!filters.brand ||
        post.brand?.toLowerCase().includes(filters.brand.toLowerCase())) &&
      (!filters.pricePerDay ||
        (pricePerDay >= parseInt(filters.pricePerDay) - 500 &&
          pricePerDay <= parseInt(filters.pricePerDay) + 500)) &&
      (!filters.pricePerWeek ||
        (pricePerWeek >= parseInt(filters.pricePerWeek) - 1500 &&
          pricePerWeek <= parseInt(filters.pricePerWeek) + 1500)) &&
      (!filters.fueltype ||
        post.fueltype?.toLowerCase() === filters.fueltype.toLowerCase())
    );
  });

  // Pagination
  const indexOfLastPost = currentPage * cardsPerPage;
  const indexOfFirstPost = indexOfLastPost - cardsPerPage;
  const currentPosts = filteredRentPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredRentPosts.length / cardsPerPage);

  const goToPage = (pageNum) => {
    setCurrentPage(pageNum);
    window.scrollTo({ top: 700, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);
    if (endPage - startPage < 2) startPage = Math.max(endPage - 2, 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const getImageUrl = (img) => {
    if (img && img.startsWith("http")) return img;
    return `${process.env.REACT_APP_API_URL}/${img}`;
  };

  const toggleExpand = (id) => setExpandedPostId(expandedPostId === id ? null : id);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <div style={{ display: "block", right: 10 }} className="slick-next" />,
    prevArrow: <div style={{ display: "block", left: 10 }} className="slick-prev" />,
  };

  // Razorpay Payment (with purpose: view_contacts)
  const openRazorpay = async () => {
    try {
      const orderRes = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: JSON.stringify({ purpose: "view_contacts" }), // Critical!
      });
      const orderData = await orderRes.json();

      return new Promise((resolve) => {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: orderData.amount,
          currency: "INR",
          name: "BikeBechoo",
          description: "Unlock 3 Contact Views (₹59)",
          order_id: orderData.id,
          handler: async (response) => {
            const verifyRes = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/verify`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userInfo.token}`,
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                purpose: "view_contacts",
              }),
            });
            const verifyData = await verifyRes.json();
            resolve(verifyData.success === true);
          },
          modal: { ondismiss: () => resolve(false) },
          theme: { color: "#3399cc" },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      });
    } catch (err) {
      console.error("Razorpay error:", err);
      return false;
    }
  };

  // Handle clicking "View Contact"
  const handleContactClick = async (e, bike) => {
    e.stopPropagation();

    if (unlockedContacts[bike._id]) return; // Already shown

    if (contactsUnlockedCount > 0) {
      // Use 1 credit
      setUnlockedContacts((prev) => ({ ...prev, [bike._id]: true }));
      setContactsUnlockedCount((prev) => prev - 1);

      // Decrement on backend
      await fetch(`${process.env.REACT_APP_API_URL}/api/payment/use-contact`, {
        method: "POST",
        headers: { Authorization: `Bearer ${userInfo.token}` },
      }).catch((err) => console.error("Failed to use contact credit:", err));
    } else {
      // No credits → trigger payment
      setPendingBike(bike);
      setShowPaymentModal(true);
    }
  };

  // After user clicks "Pay ₹59"
  const handlePaymentContinue = async () => {
    setShowPaymentModal(false);
    if (!pendingBike) return;

    const paid = await openRazorpay();
    if (!paid) {
      alert("Payment failed or cancelled.");
      return;
    }

    // Refresh credits from backend
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/contacts`, {
      headers: { Authorization: `Bearer ${userInfo.token}` },
    });
    const data = await res.json();

    if (data.success) {
      setContactsUnlockedCount(data.contactsUnlocked);
      setUnlockedContacts((prev) => ({ ...prev, [pendingBike._id]: true }));
      alert(`Payment successful! You now have ${data.contactsUnlocked} contact views left.`);
    }

    setPendingBike(null);
  };

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  return (
    <div className="rent-page">
      <h2 className="rent-heading">"Your Ride Starts Here"</h2>
      <div className="scroll-container">
        <span className="rent-subheading">
          Rent bikes easily, safely, and at the best value — all in one place.
          {isLoggedIn && contactsUnlockedCount > 0 && (
            <span> | Contact Views Left: <strong>{contactsUnlockedCount}</strong></span>
          )}
        </span>
      </div>

      {loading ? (
        <p>Loading bikes...</p>
      ) : currentPosts.length === 0 ? (
        <p>No bikes found matching your filters.</p>
      ) : (
        <>
          {currentPosts.map((bike) => {
            const isExpanded = expandedPostId === bike._id;
            const isUnlocked = !!unlockedContacts[bike._id];

            return (
              <div key={bike._id} className={`rent-card ${isExpanded ? "expanded" : ""}`}>
                <div onClick={() => toggleExpand(bike._id)} style={{ cursor: "pointer" }}>
                  <div className="rent-image-section">
                    <div className="post-id-badge">{bike.postId}</div>
                    <Slider {...sliderSettings}>
                      {bike.bikeImages?.map((img, idx) => (
                        <div key={idx}>
                          <img
                            src={getImageUrl(img)}
                            alt={`Bike ${idx + 1}`}
                            className="bike-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/300x200";
                            }}
                          />
                        </div>
                      ))}
                    </Slider>
                    <span className="badgerent">RENT</span>
                  </div>

                  <div className="bike-info">
                    <h3 className="bike-title">{bike.brand} {bike.model}</h3>
                    <p className="bike-location">{bike.area}, {bike.state}</p>
                    <div className="bike-meta">
                      <p><strong>Year:</strong> {bike.year}</p>
                      <p><strong>Price/Day:</strong> ₹{parseInt(bike.pricePerDay).toLocaleString("en-IN")}</p>
                      <p><strong>Price/Week:</strong> ₹{parseInt(bike.pricePerWeek).toLocaleString("en-IN")}</p>
                    </div>
                    <p className="bike-description"><strong>Ownership:</strong> {bike.ownership}</p>
                    <p><strong>Posted on:</strong> {new Date(bike.createdAt).toLocaleDateString("en-GB")}</p>
                  </div>
                </div>

                {isExpanded && (
                  <div className="expanded-section">
                    <p><strong>Fuel Type:</strong> {bike.fueltype}</p>
                    <p><strong>Kilometers Driven:</strong> {bike.kilometerdriven}</p>
                    <p><strong>Engine Capacity:</strong> {bike.enginecpacity}</p>
                    <p><strong>Description:</strong> {bike.anymodification}</p>
                    <p><strong>RC Number:</strong> {bike.rcNumber}</p>
                    <p><strong>Insurance:</strong> {bike.insurancestatus}</p>
                    <p><strong>Contact Name:</strong> {bike.contactName}</p>

                    {/* Contact Display Logic */}
                    {isUnlocked ? (
                      <div className="contact-unlocked">
                        <strong>📞 Contact Number:</strong> {bike.contactMobile}
                      </div>
                    ) : isLoggedIn ? (
                      <button
                        className="contact-btn"
                        onClick={(e) => handleContactClick(e, bike)}
                      >
                        {contactsUnlockedCount > 0
                          ? `View Contact (${contactsUnlockedCount} left)`
                          : "Pay ₹59 to Unlock Contacts"}
                      </button>
                    ) : (
                      <a href="https://bike-bechoo-6.onrender.com/auth/google">
                        <button className="contact-btn login-button">Login to view contact</button>
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          <div className="pagination">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              Prev
            </button>
            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={page === currentPage ? "active" : ""}
              >
                {page}
              </button>
            ))}
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Unlock 3 Contact Views</h3>
            <p>Pay <strong>₹59</strong> to view mobile numbers of <strong>3 rental bikes</strong>.</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowPaymentModal(false)}>
                Cancel
              </button>
              <button className="pay-btn" onClick={handlePaymentContinue}>
                Pay ₹59 Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentPage;
